// JWT Authentication Utility
import config from './config';

class AuthService {
  constructor() {
    this.tokenKey = 'adminToken';
    this.checkTokenExpiry();
  }

  // Get token from localStorage
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Set token in localStorage
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  // Remove token from localStorage
  removeToken() {
    localStorage.removeItem(this.tokenKey);
  }

  // Check if token is expired
  isTokenExpired(token) {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }

  // Check token expiry and auto-logout if expired
  checkTokenExpiry() {
    const token = this.getToken();
    if (token && this.isTokenExpired(token)) {
      this.logout();
      return false;
    }
    return true;
  }

  // Logout user
  logout() {
    this.removeToken();
    // Redirect to login page
    if (window.location.pathname.startsWith('/admin')) {
      window.location.href = '/admin/login';
    }
  }

  // Make authenticated API request
  async makeAuthenticatedRequest(url, options = {}) {
    const token = this.getToken();
    
    if (!token) {
      this.logout();
      throw new Error('No authentication token found');
    }

    if (this.isTokenExpired(token)) {
      this.logout();
      throw new Error('Token has expired');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      this.logout();
      throw new Error('Authentication failed');
    }

    return response;
  }

  // Verify token with server
  async verifyToken() {
    try {
      const response = await this.makeAuthenticatedRequest(`${config.url}/admin/verify`);
      return response.ok;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  // Auto-check token every 5 minutes
  startTokenCheck() {
    setInterval(() => {
      this.checkTokenExpiry();
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Stop token checking
  stopTokenCheck() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService; 